import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

/** Field sizes (design §6.3): `md` is the form field, 44 px and body-lg. `sm` is a FilterBar
 *  control, 40 px and body (15 px) from `md` up — on a phone it keeps 17 px, below 16 px iOS
 *  zooms the page into a focused field (WP-14, WP-15). */
export const FIELD_SIZE = {
  md: 'h-11 text-body-lg',
  sm: 'h-10 text-body-lg md:text-body [@media(pointer:coarse)]:h-11',
} as const

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: keyof typeof FIELD_SIZE
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({ className, size = 'md', ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      'w-full rounded-sm border border-field bg-surface-3 px-3',
      FIELD_SIZE[size],
      'text-ink-1 placeholder:text-ink-3',
      'focus-visible:border-cyan focus-visible:shadow-glow-cyan focus-visible:outline-none',
      // Invalid (aria-invalid): danger border even when focused; the field also gets a ✕ line
      // with words under it (state is never colour only, rule 21).
      'aria-[invalid=true]:border-danger',
      className,
    )}
    {...props}
  />
))
Input.displayName = 'Input'

/** A visible field caption. Pass `htmlFor` (or use `FormField`, which wires it): a caption that
 *  is not tied to its control leaves the field nameless for a screen reader (WP-16). */
export function Label({
  children,
  htmlFor,
  id,
  className,
}: {
  children: ReactNode
  htmlFor?: string
  id?: string
  className?: string
}) {
  return (
    <label htmlFor={htmlFor} id={id} className={cn('mb-1.5 block text-caption text-ink-2', className)}>
      {children}
    </label>
  )
}

/** The props `FormField` hands to its control. */
export interface FieldProps {
  id: string
  'aria-describedby'?: string
  'aria-invalid'?: true
}

/**
 * A labelled form field (WP-16, design §6.3): the visible label is tied to the control
 * (`htmlFor`/`id`), the hint and the error line are its description (`aria-describedby`), and
 * an error sets `aria-invalid` (danger border) with a ✕ line that says what to do (rule 21).
 *
 *   <FormField label="Email" hint="Рабочая почта">{(f) => <Input {...f} type="email" />}</FormField>
 */
export function FormField({
  label,
  hint,
  error,
  id: fixedId,
  className,
  children,
}: {
  label: ReactNode
  /** a caption under the field (ink-3) */
  hint?: ReactNode
  /** what is wrong and what to do; shown with ✕ in danger */
  error?: ReactNode
  /** a fixed control id (a link or a test points at it); generated otherwise */
  id?: string
  className?: string
  children: (field: FieldProps) => ReactNode
}) {
  const auto = useId()
  const id = fixedId ?? auto
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <div className={cn('min-w-0', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children({ id, 'aria-describedby': describedBy, ...(error ? { 'aria-invalid': true as const } : {}) })}
      {error && (
        <p id={errorId} className="mt-1 text-caption text-danger">
          <span aria-hidden>✕ </span>
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="mt-1 text-caption text-ink-3">
          {hint}
        </p>
      )}
    </div>
  )
}
